<template>
  <div class="container report-container">
    <div v-if="loading">
      {{ $t('intro.loading') }}
    </div>
    <div v-else>
      <div v-if="justShowGivenAnswers">
        <h1>{{ surveyName }}</h1>
        <p>{{ $t('report.forSubmission') }} <code>{{ reportSubmissionToken }}</code></p>
        <answers-given
          :question-answers="reportQuestionAnswers"
          :show-debug-info="showDebugInfo"
        />
      </div>
      <div v-else>
        <div v-if="showThankYouMessage" class="thank-you">
          <markdown-text>{{ $t('report.feedbackThankYou') }}</markdown-text>
        </div>
        <div v-else-if="!receivedResult" class="no-info-report">
          {{ $t('end.thankYouNoInfo').replace(/\\n/g, '\n') }}
        </div>
        <div v-else-if="receivedResult">
          <full-report
            :consideration-level="considerationLevel"
            :high-risk-indicator="highRiskIndicator"
            :bdd-indicator="bddIndicator"
            :impulsivity-indicator="impulsivityIndicator"
            :referral-form="referralForm"
            :advices="advices"
            :question-answers="reportQuestionAnswers"
            :show-debug-info="showDebugInfo"
            @send-report="openSendDialog"
          />
          <floating-panel v-if="feedbackPanelAnswer === null">
            <p class="feedback-question">
              {{ $t('report.improveSurveyQuestion') }}
              <app-button
                class="ms-2"
                color="white"
                size="small"
                @click="onAnswerFeedbackPanel(false)"
              >
                {{ $t('report.buttonNo') }}
              </app-button>
              <app-button
                class="ms-2"
                color="blue"
                size="small"
                @click="onAnswerFeedbackPanel(true)"
              >
                {{ $t('report.buttonYes') }}
              </app-button>
            </p>
          </floating-panel>
        </div>
      </div>
    </div>

    <lightbox
      background="#000000"
      :open="!loading && askContactQuestion && contactQuestionAnswer !== false" :opacity="0.95"
    >
      <div class="lightbox-content-inner">
        <p>
          <markdown-text>{{ $t('report.allowCheckinQuestion') }}</markdown-text>
        </p>

        <p v-if="contactQuestionAnswer === null">
          <app-button color="white" @click="onEmailConsentClick(false)">
            {{ $t('report.buttonNo') }}
          </app-button>
          <app-button class="ms-2" @click="onEmailConsentClick(true)">
            {{ $t('report.buttonYes') }}
          </app-button>
        </p>
        <div v-else-if="contactQuestionAnswer === true">
          <strong class="me-4">{{ $t('report.provideYourEmail') }}:</strong>
          <input v-model="contactEmail" class="me-4 form-control" type="text" />
          <app-button
            :disabled="!canAcceptContactEmail || acceptingContactEmail"
            @click="acceptContactEmail"
          >
            {{ acceptingContactEmail ? $t('report.contactAccepting') : $t('report.contactAccept') }}
          </app-button>
          <div class="mt-4">
            <markdown-text>{{ $t('report.feedbackQuestionEmailNote') }}</markdown-text>
          </div>
        </div>
      </div>
    </lightbox>
    <lightbox background="#000000" :open="!loading && showContinueWarning">
      <div class="lightbox-content-inner">
        <p>
          <markdown-text>{{ $t('report.feedbackQuestionPreWarning') }}</markdown-text>
        </p>

        <p>
          <app-button color="white" @click="onContinueWarningAnswer(false)">
            {{ $t('report.feedbackQuestionPreWarningNo') }}
          </app-button>
          <app-button class="ms-2" @click="onContinueWarningAnswer(true)">
            {{ $t('report.feedbackQuestionPreWarningYes') }}
          </app-button>
        </p>
      </div>
    </lightbox>

    <lightbox
      background="#000000"
      :open="sendDialogOpen" :opacity="0.95"
    >
      <div class="lightbox-content-inner">
        <h1>{{ $t('report.buttonSendReport') }}</h1>
        <p>{{ $t('report.sendReportParagraph') }}</p>
        <p class="note">
          {{ $t('report.sendReportGdprNotice') }}
        </p>

        <div>
          <input v-model="sendEmailAddress" class="form-control" type="email" :placeholder="$t('report.sendReportEmailAddress')" />
        </div>

        <div class="mt-4">
          <app-button color="white" @click="sendDialogOpen = false">
            {{ $t(sent ? 'report.sendReportButtonClose' : 'report.sendReportButtonCancel') }}
          </app-button>
          <app-button class="ms-2" :disabled="!sendEmailAddressOk || sending || sent" @click="sendReport">
            {{ $t(sendButtonMessage) }}
          </app-button>
        </div>
      </div>
    </lightbox>
  </div>
</template>

<script>
import { mapState } from 'pinia';
import { useSurveyStore } from '@/store';
import * as EmailValidator from 'email-validator';

import FullReport from '@/components/Report/FullReport';
import ReportMixins from '@/components/Report/ReportMixins';
import FloatingPanel from '@/components/common/FloatingPanel';
import AppButton from '@/components/common/AppButton';
import Lightbox from '@/components/common/Lightbox';
import MarkdownText from '@/components/common/MarkdownText';
import AnswersGiven from '@/components/Report/AnswersGiven';

export default {
  name: 'FullReportView',
  components: {
    AnswersGiven, MarkdownText, FullReport, FloatingPanel, AppButton, Lightbox,
  },
  mixins: [ReportMixins],
  data() {
    return {
      feedbackPanelAnswer: null,

      showThankYouMessage: false,

      askContactQuestion: false,
      contactQuestionAnswer: null,
      contactEmail: null,
      acceptingContactEmail: false,

      showContinueWarning: false,

      showDebugInfo: true,

      sendDialogOpen: false,
      sending: false,
      sent: false,

      sendEmailAddress: '',
    };
  },
  computed: {
    ...mapState(useSurveyStore, {
      language: 'language',
    }),
    action() {
      return this.$route?.params?.action || null;
    },
    canAcceptContactEmail() {
      return this.contactEmail && EmailValidator.validate(this.contactEmail);
    },
    sendEmailAddressOk() {
      if (!this.sendEmailAddress) {
        return false;
      }

      // Simple validation, check for an at symbol as well as a dot
      return !!this.sendEmailAddress.match(/^.+?@.+?\..+?$/);
    },
    sendButtonMessage() {
      if (this.sent) {
        return this.$t('report.sendReportButtonConfirmed');
      }

      if (this.sending) {
        return this.$t('report.sendReportButtonConfirming');
      }

      return this.$t('report.sendReportButtonConfirm');
    },
  },
  methods: {
    openSendDialog() {
      this.sendEmailAddress = '';
      this.sendDialogOpen = true;
      this.sent = false;
    },
    sendReport() {
      if (this.sending) {
        return;
      }

      this.sending = true;

      this.$apiClient.misc.sendReport(this.reportSubmissionToken, this.sendEmailAddress, this.language).then(() => {
        this.sending = false;

        this.sent = true;
      });
    },
    onContinueWarningAnswer(answer) {
      this.showContinueWarning = false;

      if (!answer) {
        return;
      }

      this.feedbackPanelAnswer = true;

      if (this.isAnonymous) {
        this.$router.push('/survey/clientFeedback');
      } else {
        this.showThankYouMessage = true;
      }
    },
    onAnswerFeedbackPanel(answer) {
      if (answer) {
        this.$apiClient.misc.acceptConsent('feedbackSurvey', this.reportSubmissionToken).then(() => {
          this.showContinueWarning = true;
        });
      } else {
        this.feedbackPanelAnswer = false;

        this.$apiClient.misc.denyConsent('feedbackSurvey', this.reportSubmissionToken).then(() => {
          // No consent
        });
      }
    },
    onEmailConsentClick(consent) {
      if (consent) {
        this.contactQuestionAnswer = true;
      } else if (this.reportSubmissionToken) {
        // We can only set no contact with a submission token
        // Else TODO: cookies
        this.$apiClient.misc.denyConsent('contactEmail', this.reportSubmissionToken).then(() => {
          this.acceptingContactEmail = false;
          this.askContactQuestion = false;
        }).catch((e) => {
          this.acceptingContactEmail = false;
        });
      }
      console.log('consent', consent);
    },
    acceptContactEmail() {
      this.acceptingContactEmail = true;

      this.$apiClient.misc.acceptConsent('contactEmail', this.reportSubmissionToken, this.contactEmail).then(() => {
        this.acceptingContactEmail = false;
        this.askContactQuestion = false;
      }).catch((e) => {
        this.acceptingContactEmail = false;
      });
    },
    onUserData(userAnswersData) {
      if (this.justShowGivenAnswers) {
        return;
      }

      if (this.action === 'view') {
        return;
      }

      if (this.consent.feedbackSurvey === false) {
        // Remember if the user said they didn't want to
        this.feedbackPanelAnswer = false;
      }

      if (this.consent.contactEmail === null) {
        // Removed as of 2022-12-13
        this.askContactQuestion = false;
        // this.askContactQuestion = true;
      }
    },
  },
};
</script>

<style lang="scss">
.lightbox-content-inner {
  p.note {
    font-size: 90%;
    color: #ababab;
    text-align: left;
  }
}
</style>
<style lang="scss" scoped>
@import "../sass/no-info-report";

@media print {
  .floating-panel {
    display: none !important;
  }
}

.feedback-question {
  display: inline-block;
  width: auto;
  margin: 0;
}

.thank-you {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 20pt;
  text-align: center;
}

</style>
